import { ColumnDef } from "@tanstack/react-table";
import { AxiosError, AxiosResponse } from "axios";
import React, { useEffect, useState, useRef, useMemo } from "react";
import DataTable2 from "shared/DataTable2";
import { useServerPaginatedList } from "../../shared/useServerPaginatedList";
import { ServerDeletedFilterValue } from "../../shared/ServerListFilters";
import ServerDeletedPills from "../../shared/ServerDeletedPills";
import ServerStatusPills from "../../shared/ServerStatusPills";
import { Button } from "../../components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../../components/ui/dialog";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "../../components/ui/accordion"

import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/Label";
import { Toaster } from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import { VipAxiosInstance } from "../../Network/VipAxiosInstance";
import { LoginAxiosInstance } from "../../Network/LoginAxiosInstance";
import { ScrollArea } from "../../components/ui/scroll-area";
import { vip, Result } from "../../interface/Vip";
import FileUpload from "Pages/uploadFile/Vip/Vip";

import { Option } from "../../interface/Option";
import ListOfNewNetworkName from "../Shared/NewNetworkName";
import PolicyNames from "../Shared/PolicyNames";
import VipPatientSelector from "Pages/Shared/VipPatient";
import { PatientRelationAxiosInstance } from "../../Network/PatientRelationAxiosInstance";
import { patient } from "interface/Patient";
import useQaAutomationIds from "../../utils/useQaAutomationIds";

// ─── Role Helper ──────────────────────────────────────────────────────────────
const getUserRole = (): string => {
	try {
		const token = localStorage.getItem("token") || "";
		const payload = JSON.parse(atob(token.split(".")[1]));
		return payload.userType || "";
	} catch { return ""; }
};


// ─── Backend / relational-schema normalization ───────────────────────────────
const isVipActive = (value: any): boolean =>
	value === true ||
	value === 1 ||
	value === "1" ||
	value === "true" ||
	value === "Active" ||
	value === "active";

// Linked patient's live status is separate from the VIP list status.
const patientIsInactive = (value: any): boolean => {
  if (!value || typeof value !== "object") return false;
  const flag = value.is_active ?? value.Is_active ?? value.isActive ?? value.active;
  const status = String(value.Status ?? value.status ?? "").trim().toLowerCase();
  return value.isInactive === true || value.__inactive === true ||
    flag === false || flag === 0 || flag === "false" || flag === "0" ||
    status === "inactive" || status === "disabled" || status === "false" || status === "0" ||
    value.is_deleted === true;
};

const patientHasStatus = (value: any): boolean => !!value && typeof value === "object" &&
  ["is_active", "Is_active", "isActive", "active", "Status", "status", "isInactive", "__inactive", "is_deleted"]
    .some(key => value[key] !== undefined && value[key] !== null);

const normalizeVipPatients = (raw: any): any[] => {
	const list = Array.isArray(raw) ? raw : raw == null ? [] : [raw];

	return list
		.filter(Boolean)
		.map((patient: any) => {
			if (typeof patient !== "object") {
				const numericId = /^\d+$/.test(String(patient))
					? Number(patient)
					: undefined;

				return {
					id: numericId,
					patient_id: numericId,
					name: numericId ? `Patient #${numericId}` : String(patient),
				};
			}

			const patientId =
				patient.patient_id ??
				patient.Patient_id ??
				patient.id ??
				null;

			const name =
				patient.name ??
				patient.Full_name ??
				patient.full_name ??
				[
					patient.First_name ?? patient.first_name,
					patient.Last_name ?? patient.last_name,
				].filter(Boolean).join(" ") ??
				"";

			return {
				...patient,
				id: patient.id ?? patientId,
				patient_id: patientId,
				name: String(name || (patientId ? `Patient #${patientId}` : "")),
				National_id:
					patient.National_id ??
					patient.national_id ??
					null,
			};
		});
};

const normalizeVipRow = (item: any): Result => {
	const patients = normalizeVipPatients(
		item?.patients ??
		item?.Patients ??
		item?.patient ??
		item?.Patient ??
		[]
	);

	return {
		...item,
		id: item?.id,
		name: item?.Name ?? item?.name ?? "",
		status: isVipActive(item?.Status ?? item?.status),
		patients,
		patients_names_string: patients.map((p: any) => p.name).join(" "),
		created_by:
			item?.created_by ??
			item?.Created_by ??
			item?.createdBy ??
			null,
		createdat:
			item?.createdat ??
			item?.created_at ??
			item?.createdAt ??
			null,
		updated_by: Array.isArray(
			item?.updated_by ??
			item?.Updated_by ??
			item?.updatedBy
		)
			? (item?.updated_by ?? item?.Updated_by ?? item?.updatedBy)
			: [],
		updatedat:
			item?.updatedat ??
			item?.updated_at ??
			item?.updatedAt ??
			null,
		is_deleted: Boolean(
			item?.is_deleted ??
			item?.Is_deleted ??
			item?.deleted ??
			false
		),
		deleted_by: Array.isArray(
			item?.deleted_by ??
			item?.Deleted_by ??
			item?.deletedBy
		)
			? (item?.deleted_by ?? item?.Deleted_by ?? item?.deletedBy)
			: [],
	} as Result;
};


const formatVipPatientLabel = (patient: any): string => {
	const name =
		patient?.name ??
		patient?.Full_name ??
		patient?.full_name ??
		[
			patient?.First_name ?? patient?.first_name,
			patient?.Last_name ?? patient?.last_name,
		].filter(Boolean).join(" ") ??
		"";

	const identifiers: string[] = [];

	const nationalId =
		patient?.National_id ??
		patient?.national_id ??
		null;
	const memberId =
		patient?.Member_id ??
		patient?.member_id ??
		null;
	const passport =
		patient?.Patient_passport ??
		patient?.patient_passport ??
		patient?.passport ??
		null;

	if (nationalId !== null && nationalId !== undefined && String(nationalId).trim() !== "") {
		identifiers.push(`National ID: ${nationalId}`);
	}
	if (memberId !== null && memberId !== undefined && String(memberId).trim() !== "") {
		identifiers.push(`Member ID: ${memberId}`);
	}
	if (passport !== null && passport !== undefined && String(passport).trim() !== "") {
		identifiers.push(`Passport: ${passport}`);
	}

	const safeName = String(name || `Patient #${patient?.patient_id ?? patient?.id ?? ""}`).trim();
	return identifiers.length > 0
		? `${safeName} (${identifiers.join(" | ")})`
		: safeName;
};

const patientIdFromValue = (value: any): number | null => {
	if (value === null || value === undefined || value === "") return null;

	if (typeof value === "number" && Number.isFinite(value)) {
		return value;
	}

	if (typeof value === "string" && /^\d+$/.test(value.trim())) {
		return Number(value.trim());
	}

	if (typeof value === "object") {
		const candidate =
			value.patient_id ??
			value.Patient_id ??
			value.id ??
			value.value;

		if (typeof candidate === "number" && Number.isFinite(candidate)) {
			return candidate;
		}

		if (
			typeof candidate === "string" &&
			/^\d+$/.test(candidate.trim())
		) {
			return Number(candidate.trim());
		}
	}

	return null;
};

// Strict name validation used by VIP List Name and the embedded Patient names.
// Every word must start with a capital letter. Hyphenated words are supported.
const STRICT_NAME_PATTERN =
	/^[A-Z][a-zA-Z]*(?:-[A-Z][a-zA-Z]*)*(?:\s+[A-Z][a-zA-Z]*(?:-[A-Z][a-zA-Z]*)*)*$/;

const STRICT_NAME_FORMAT_ERROR =
	"Invalid name. Only letters are allowed and each word must start with a capital letter.";

const validateStrictName = (
	value: string,
	requiredMessage: string,
	minimumMessage: string,
): string => {
	const trimmed = value.trim();
	if (!trimmed) return requiredMessage;
	if (trimmed.length < 3) return minimumMessage;
	if (!STRICT_NAME_PATTERN.test(trimmed)) return STRICT_NAME_FORMAT_ERROR;
	return "";
};

const VIP: React.FC = () => {
  useQaAutomationIds("vip");
	const [items, setItems] = useState<vip>({ result: [] });
	const [patientItems, setPatientItems] = useState<patient>({ result: [] });

	const [gender, setGender] = useState<string>("");
	const [firstname, setfirstname] = useState<string>("");
	const [lastname, setlastname] = useState<string>("");
	const [fullname, setfullname] = useState<string>("");
	const [activenetworks, setactivenetworks] = useState<Option[]>([]);
	const [activepolices, setactivepolices] = useState<Option[]>([]);
	const [totalcoverage, settotalcoverage] = useState<number | undefined>();
	const [currentspending, setcurrentspending] = useState<number | undefined>();
	const [active, setactive] = useState<boolean>(true);
	const [patientName, setPatientName] = useState("");
	const [patientIdentifier, setPatientIdentifier] = useState("");
	const [dob, setDob] = useState("");
	const [totalCoverage, setTotalCoverage] = useState(0);
	const [currentSpending, setCurrentSpending] = useState(0);
	const [vipListName, setVipListName] = useState("");
	const [status, setStatus] = useState("active");
	const [isPatientExisting, setIsPatientExisting] = useState(false);
	const [vipStatus, setVipStatus] = useState("active");

	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [editIndex, setEditIndex] = useState<null | number>(null);
	const [clearNetworks, setClearNetworks] = useState(false);
	const [clearPolicy, setClearPolicy] = useState(false);
	const [showAllUpdates, setShowAllUpdates] = useState(false);
	
	const [selectedItem, setSelectedItem] = useState<Result | null>(null);
	const dialogRef = useRef<HTMLDialogElement | null>(null);

	const [selectedPatients, setSelectedPatients] = useState<any[]>([]);
	const patientDialogRef = useRef<HTMLDialogElement | null>(null);

	const token = localStorage.getItem("token") || "";

	const userRole = getUserRole();
	const canManageDeleted = userRole === "system_admin" || userRole === "super_super_admin";

	const [forceDialogOpen, setForceDialogOpen] = useState(false);
	const [forceAction, setForceAction] = useState<"softDelete" | "hardDelete" | "status" | null>(null);
	const [forceId, setForceId] = useState<string | number | null>(null);
	const [forceError, setForceError] = useState<string>("");

	const [usersMap, setUsersMap] = useState<Record<number, string>>({});

	const [clearPatient, setClearPatient] = useState(false);
	const [patient, setPatient] = useState<Option[]>([]);
  const [livePatients, setLivePatients] = useState<Record<string, any>>({});
  const patientLookupRequest = useRef(0);

  const enrichPatients = (raw: any[], lookup: Record<string, any> = livePatients): any[] =>
    normalizeVipPatients(raw).map((p: any) => {
      const id = patientIdFromValue(p);
      const current = id !== null ? lookup[String(id)] : undefined;
      if (!current) return p;
      // Use current status, but retain member-specific information from VIP relation.
      const enriched = { ...p, ...current, id: p.id ?? current.id, patient_id: p.patient_id ?? current.patient_id };
      if (!patientHasStatus(current)) return p;
      return { ...enriched, isInactive: patientIsInactive(current), __inactive: patientIsInactive(current),
        is_active: !patientIsInactive(current), name: p.name || current.name };
    });

  const refreshPatientStatuses = async (raw: any[]): Promise<Record<string, any>> => {
    const ids = Array.from(new Set(normalizeVipPatients(raw)
      .map((p: any) => patientIdFromValue(p))
      .filter((id): id is number => id !== null)));
    const entries = await Promise.all(ids.map(async id => {
      try {
        const response = await PatientRelationAxiosInstance.get(`getPatient/${id}`);
        const payload = response.data;
        const source = payload?.result ?? payload?.patient ?? payload?.data ?? payload;
        const record = Array.isArray(source) ? source.find((v: any) => String(v?.id ?? v?.patient_id) === String(id)) : source;
        return record && typeof record === "object" && patientHasStatus(record)
          ? [String(id), record] as const : null;
      } catch { return null; }
    }));
    const next: Record<string, any> = {};
    entries.forEach(entry => { if (entry) next[entry[0]] = entry[1]; });
    if (Object.keys(next).length) setLivePatients(previous => ({ ...previous, ...next }));
    return next;
  };


	const handlePatientChange = (patients: Option[]) => {
		setPatient(patients);
	};

	const getSelectedPatientIds = (options: Option[] = patient): number[] => {
		const resolvedIds = options.map((option: any) => patientIdFromValue(option));

		if (resolvedIds.some((id) => id === null)) {
			throw new Error("Each selected patient must contain a valid patient ID.");
		}

		return Array.from(new Set(resolvedIds as number[]));
	};

	const { toast } = useToast();

	const [advancedSearch, setAdvancedSearch] = useState({
		search: "",
		status: ""
	});
	const [filterDeleted, setFilterDeleted] = useState<ServerDeletedFilterValue>("false");

	const [isPatDialogOpen, setIsPatDialogOpen] = useState(false);

	const [nationalId, setNationalId] = useState<number | undefined>();
	const [memberId, setMemberId] = useState<number | undefined>();
	const [patientPassport, setPatientPassport] = useState<string | undefined>();
	const [patientDob, setPatientDob] = useState<string>("");
	const [firstName, setFirstName] = useState<string>("");
	const [lastName, setLastName] = useState<string>("");
	const [fullName, setFullName] = useState<string>("");
	const [policy, setPolicy] = useState<Option | null>(null);
	const [identifierType, setIdentifierType] = useState<string>("National ID");
	const [identifierValue, setIdentifierValue] = useState<number | undefined>();
	const [pstatus, setPstatus] = useState<boolean>(true);
	const [firstNameError, setFirstNameError] = useState("")
	const [lastNameError, setLastNameError] = useState("")
	const [identifierError, setIdentifierError] = useState("")
	const [duplicatePatient, setDuplicatePatient] = useState<any | null>(null);
  const [checkingPatientIdentifiers, setCheckingPatientIdentifiers] = useState(false);
  const [patientLookupError, setPatientLookupError] = useState("");
  const patientLookupSequence = useRef(0);

  // Read all pages rather than checking only the currently visible patient list.
  const findExistingPatient = async (
    national: number | undefined,
    member: number | undefined,
    passport: string | undefined,
  ): Promise<any | null> => {
    const normalize = (value: any) => String(value ?? "").trim().toUpperCase();
    const identifiers = [
      ["National_id", national],
      ["Member_id", member],
      ["Patient_passport", passport],
    ].filter(([, value]) => normalize(value) !== "");
    if (!identifiers.length) return null;

    const pageSize = 100;
    for (let page = 1; page <= 1000; page++) {
      const response = await PatientRelationAxiosInstance.get("getPatient", {
        params: { page, limit: pageSize, is_deleted: "false" },
      });
      if (response.data?.success === false) {
        throw new Error(response.data?.message || "Unable to check existing patients.");
      }
      const payload = response.data;
      const raw = payload?.result ?? payload?.patients ?? payload?.data ?? payload;
      const rows: any[] = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.result) ? raw.result
        : Array.isArray(raw?.rows) ? raw.rows
        : Array.isArray(raw?.data) ? raw.data
        : [];
      if (!Array.isArray(raw) && !Array.isArray(raw?.result) &&
          !Array.isArray(raw?.rows) && !Array.isArray(raw?.data)) {
        throw new Error("Patient lookup returned an unsupported response format.");
      }
      const match = rows.find((row) => identifiers.some(([field, value]) => {
        const keys = field === "National_id"
          ? ["National_id", "national_id"]
          : field === "Member_id"
            ? ["Member_id", "member_id"]
            : ["Patient_passport", "patient_passport", "passport"];
        return keys.some((key) => normalize(row?.[key]) !== "" && normalize(row?.[key]) === normalize(value));
      }));
      if (match) return match;
      const totalPages = Number(payload?.totalPages ?? payload?.pagination?.totalPages ?? raw?.totalPages);
      if (Number.isFinite(totalPages) && totalPages > 0 && page >= totalPages) return null;
      if (rows.length < pageSize) return null;
    }
    throw new Error("Patient lookup exceeded the supported pagination limit.");
  };

  useEffect(() => {
    if (!isPatDialogOpen) return;
    const sequence = ++patientLookupSequence.current;
    setDuplicatePatient(null);
    setPatientLookupError("");
    if (!hasPatientIdentifier()) {
      setCheckingPatientIdentifiers(false);
      return;
    }
    setCheckingPatientIdentifiers(true);
    const timer = setTimeout(async () => {
      try {
        const found = await findExistingPatient(nationalId, memberId, patientPassport);
        if (sequence !== patientLookupSequence.current) return;
        setDuplicatePatient(found);
        const message = found ? "This identifier already belongs to an existing patient." : "";
        setIdentifierError(nationalId !== undefined && found && String(found.National_id ?? found.national_id ?? "") === String(nationalId) ? message : "");
        setMidError(memberId !== undefined && found && String(found.Member_id ?? found.member_id ?? "") === String(memberId) ? message : "");
        setPpError(patientPassport && found && String(found.Patient_passport ?? found.patient_passport ?? found.passport ?? "").trim().toUpperCase() === patientPassport.trim().toUpperCase() ? message : "");
      } catch (error: any) {
        if (sequence !== patientLookupSequence.current) return;
        setPatientLookupError("Could not verify patient identifiers. Please retry before saving.");
      } finally {
        if (sequence === patientLookupSequence.current) setCheckingPatientIdentifiers(false);
      }
    }, 450);
    return () => clearTimeout(timer);
  }, [nationalId, memberId, patientPassport, isPatDialogOpen]);

	const [firstError, setFirstError] = useState<string>("");
	const [lastError, setLastError] = useState<string>("");
	const [dobError, setDobError] = useState("");
	const [totalError, setTotalError] = useState("");
	const [currentError, setCurrentError] = useState("");
	const [genderError, setGenderError] = useState("");
	const [midError, setMidError] = useState("");
	const [ppError, setPpError] = useState("");
	const [identifierGroupError, setIdentifierGroupError] = useState("");
	const [nameError, setNameError] = useState<string>("");

	const fetchUsers = async () => {
		try {
			const res = await LoginAxiosInstance.get("/");
			const raw: any[] = res.data?.users ?? res.data?.user ?? res.data ?? [];
			const map: Record<number, string> = {};
			raw.forEach((u: any) => {
				if (u.id != null) map[Number(u.id)] = u.name ?? u.username ?? String(u.id);
			});
			setUsersMap(map);
		} catch {}
	};

	const resolveUserName = (val: any): string => {
		if (val == null) return "—";
		if (typeof val === "object") return val.name ?? val.username ?? "—";
		if (typeof val === "string" && isNaN(Number(val))) return val;
		return usersMap[Number(val)] ?? String(val);
	};

	const handleDownload = (item: Result) => {
		const rows = ["name;status;patients"];
		const patientsList = item.patients?.map(p => p.name).join(", ") || "";
		rows.push([item.name || "", (item.status === true || item.status === "Active") ? "TRUE" : "FALSE", patientsList].join(";"));
		const csvContent = rows.join("\n");
		const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `VIP${item.id}.csv`;
		a.click();
		window.URL.revokeObjectURL(url);
	};

	const handleDobChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setPatientDob(value);
		if (!value) { setDobError("Date of Birth is required"); return; }
		const [year, month, day] = value.split("-");
		const formattedDate = `${month}/${day}/${year}`;
		const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
		if (!dateRegex.test(formattedDate)) { setDobError("Date format must be mm/dd/yyyy"); return; }
		const selectedDate = new Date(value);
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		if (selectedDate >= today) { setDobError("Date of Birth cannot be in the future"); return; }
		setDobError("");
	};

	const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setFirstName(value);
		setFirstError(
			validateStrictName(
				value,
				"First Name is required.",
				"Name must be at least 3 characters long",
			),
		);
	};

	const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setLastName(value);
		setLastError(
			validateStrictName(
				value,
				"Last Name is required.",
				"Name must be at least 3 characters long",
			),
		);
	};

	const hasPatientIdentifier = (
		national: number | undefined = nationalId,
		member: number | undefined = memberId,
		passport: string | undefined = patientPassport,
	): boolean => {
		return Boolean(
			(typeof national === "number" && national > 0) ||
			(typeof member === "number" && member > 0) ||
			passport?.trim(),
		);
	};

	const validatePatientIdentifiers = (): boolean => {
		if (!hasPatientIdentifier()) {
			setIdentifierGroupError(
				"At least one patient identifier (National ID, Member ID, or Passport Number) must be provided.",
			);
			return false;
		}

		setIdentifierGroupError("");
		return true;
	};

	const handleIDChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value.trim();

		if (value === "") {
			setNationalId(undefined);
			setIdentifierError("");
			if (hasPatientIdentifier(undefined, memberId, patientPassport)) {
				setIdentifierGroupError("");
			}
			return;
		}

		if (!/^\d+$/.test(value)) {
			setIdentifierError("Identifier must contain numbers only. Letters or symbols are not allowed.");
			setNationalId(undefined);
			return;
		}


		setIdentifierError("");
		setNationalId(Number(value));
		setIdentifierGroupError("");
	};

	const handleID2Change = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value.trim();

		if (value === "") {
			setMemberId(undefined);
			setMidError("");
			if (hasPatientIdentifier(nationalId, undefined, patientPassport)) {
				setIdentifierGroupError("");
			}
			return;
		}

		if (!/^\d+$/.test(value)) {
			setMidError("Identifier must contain numbers only. Letters or symbols are not allowed.");
			setMemberId(undefined);
			return;
		}


		setMidError("");
		setMemberId(Number(value));
		setIdentifierGroupError("");
	};

	const handleID3Change = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		const trimmedValue = value.trim();
		const passportRegex = /^[A-Za-z0-9\- ]+$/;

		if (trimmedValue === "") {
			setPatientPassport(undefined);
			setPpError("");
			if (hasPatientIdentifier(nationalId, memberId, undefined)) {
				setIdentifierGroupError("");
			}
			return;
		}

		if (!passportRegex.test(value)) {
			setPpError("Passport may contain only letters, numbers, spaces and hyphens.");
			setPatientPassport(undefined);
			return;
		}


		setPpError("");
		setPatientPassport(value);
		setIdentifierGroupError("");
	};

	const handleTotalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		if (value.trim() === "") { setTotalError("Total is required."); return; }
		if (!/^\d+$/.test(value)) { setTotalError("Identifier must contain numbers only. Letters or symbols are not allowed."); return; }
		
		setTotalError("");
		setTotalCoverage(Number(value));
	};

	const handleCurrentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		if (value.trim() === "") { setCurrentError("Total is required."); return; }
		if (!/^\d+$/.test(value)) { setCurrentError("Identifier must contain numbers only. Letters or symbols are not allowed."); return; }
		
		setCurrentError("");
		setCurrentSpending(Number(value));
	};

	const handlePolicyChange = (selectedOptions: Option | Option[] | null) => {
		if (!selectedOptions) { setPolicy(null); return; }
		const lastSelected = Array.isArray(selectedOptions) ? selectedOptions[selectedOptions.length - 1] : selectedOptions;
		setPolicy(lastSelected);
		setClearPolicy(true);
		setTimeout(() => { setClearPolicy(false); }, 10);
	};

	useEffect(() => {
		fetchUsers();
	}, []);

	const clear = () => {
		setGender("");
		setfirstname("");
		setlastname("");
		setfullname("");
		setactivenetworks([]);
		setactivepolices([]);
		setcurrentspending(0);
		settotalcoverage(0);
		setactive(true);
		setPatientIdentifier("");
		setPatientName("");
		setDob("");
		setVipListName("");
		setVipStatus("active");
		setPatient([]);
		setNameError("");
	};

	const fetchVipDetails = async (
		vipId: string | number,
		fallback?: Result,
	): Promise<Result> => {
		const response = await VipAxiosInstance.get(`getVip/${vipId}`);

		const rawResult =
			response.data?.result ??
			response.data?.vip ??
			response.data?.data ??
			response.data;

		const rawVip = Array.isArray(rawResult) ? rawResult[0] : rawResult;

		if (!rawVip || typeof rawVip !== "object") {
			throw new Error("VIP details were not returned by the backend.");
		}

		return normalizeVipRow({
			...(fallback || {}),
			...rawVip,
		});
	};

	const handleOpenDialog = async (item: Result): Promise<void> => {
		setShowAllUpdates(false);

		try {
			const details = await fetchVipDetails(item.id, item);
			const current = await refreshPatientStatuses((details as any).patients || []);
			setSelectedItem(normalizeVipRow({ ...details, patients: enrichPatients((details as any).patients || [], { ...livePatients, ...current }) }));
		} catch (error) {
			console.error("Failed to load VIP details:", error);
			setSelectedItem(normalizeVipRow(item));
			toast({
				title: "Warning",
				description:
					"VIP details could not be refreshed. Showing the available row data.",
			});
		}
	};

	const handleCloseDialog = () => { setSelectedItem(null); setShowAllUpdates(false); };

	const handleOpenPatientDialog = (patients: any[]) => {
		setSelectedPatients(patients);
		patientDialogRef.current?.showModal();
	};

	const handleClosePatientDialog = () => {
		patientDialogRef.current?.close();
		setSelectedPatients([]);
	};

	const handleAddClick = () => {
		setEditIndex(null);
		clear();
		setIsDialogOpen(true);
	};

	const handleEditClick = async (item: Result): Promise<void> => {
		try {
			const details = await fetchVipDetails(item.id, item);
			const current = await refreshPatientStatuses((details as any).patients || []);
			handleEdit(normalizeVipRow({ ...details, patients: enrichPatients((details as any).patients || [], { ...livePatients, ...current }) }));
		} catch (error) {
			console.error("Failed to load VIP details for edit:", error);
			handleEdit(normalizeVipRow(item));
			toast({
				title: "Warning",
				description:
					"VIP details could not be refreshed. Editing the available row data.",
			});
		}

		setIsDialogOpen(true);
	};

	const handleNetworksChange = (selectedOptions: Option | Option[] | null) => {
		if (!selectedOptions) { setactivenetworks([]); return; }
		setactivenetworks(Array.isArray(selectedOptions) ? selectedOptions : [selectedOptions]);
	};

	const handlePoliciesChange = (selectedOptions: Option | Option[] | null) => {
		if (!selectedOptions) { setactivepolices([]); return; }
		setactivepolices(Array.isArray(selectedOptions) ? selectedOptions : [selectedOptions]);
	};

	const clear2 = () => {
		setNationalId(undefined);
		setMemberId(undefined);
		setPatientPassport(undefined);
		setPatientDob("");
		setGender("");
		setFirstName("");
		setLastName("");
		setFullName("");
		setPolicy(null);
		setCurrentSpending(0);
		setTotalCoverage(0);
		setPstatus(true);

		// Clear all Add Patient validation / duplicate-check messages when the modal closes.
		setFirstError("");
		setLastError("");
		setIdentifierError("");
		setMidError("");
		setPpError("");
		setDobError("");
		setTotalError("");
		setCurrentError("");
		setGenderError("");
		setIdentifierGroupError("");
		setDuplicatePatient(null);
		setPatientLookupError("");
		setCheckingPatientIdentifiers(false);
	};

	const addItem2 = async (): Promise<void> => {
		setFirstError("");
		setLastError("");
		setIdentifierError("");
		setTotalError("");
		setCurrentError("");
		setGenderError("");

		const identifiersAreValid = validatePatientIdentifiers();

		const firstNameValidationError = validateStrictName(
			firstName,
			"First Name is required.",
			"Name must be at least 3 characters long",
		);
		const lastNameValidationError = validateStrictName(
			lastName,
			"Last Name is required.",
			"Name must be at least 3 characters long",
		);

		setFirstError(firstNameValidationError);
		setLastError(lastNameValidationError);

		if (firstNameValidationError || lastNameValidationError || totalCoverage.toString() === "" || currentSpending.toString() === "") {
			if (totalCoverage.toString() === "0") setTotalError("Total Coverage is required.");
			if (currentSpending.toString() === "0") setCurrentError("Current Spending is required.");
			if (gender.toString() === "") setGenderError("Gender is required.");
			return;
		}

		if (!identifiersAreValid) return;

		if (dobError || totalError || currentError || genderError) return;

    setCheckingPatientIdentifiers(true);
    try {
      const found = await findExistingPatient(nationalId, memberId, patientPassport);
      setDuplicatePatient(found);
      if (found) {
        toast({ title: "Existing Patient", description: "This identifier is already registered. Select the existing patient instead.", variant: "destructive" });
        return;
      }
      setPatientLookupError("");
    } catch (error) {
      setPatientLookupError("Could not verify patient identifiers. Please retry before saving.");
      return;
    } finally {
      setCheckingPatientIdentifiers(false);
    }

		try {
			const policyValue = policy ? [policy.value] : [];
			const newPatientData = {
				National_id: nationalId,
				Member_id: memberId,
				Patient_passport: patientPassport,
				Patient_dob: patientDob,
				Gender: gender,
				First_name: firstName,
				Last_name: lastName,
				Full_name: `${firstName} ${lastName}`,
				Policy: policyValue,
				Total_Coverage: totalCoverage,
				Current_spending: currentSpending,
				Status: pstatus,
				token: token,
			};

			const response = await PatientRelationAxiosInstance.post("addNewPatient", newPatientData);

			if (response.data.success) {
				const createdPatient =
					response.data?.result ??
					response.data?.patient ??
					response.data?.data ??
					null;

				const createdPatientId = patientIdFromValue(createdPatient);
				const createdPatientName =
					createdPatient?.Full_name ??
					createdPatient?.full_name ??
					`${firstName} ${lastName}`.trim();

				if (createdPatientId !== null) {
					const newOption: Option = {
						value: createdPatientId as any,
						label: formatVipPatientLabel({
							...(createdPatient && typeof createdPatient === "object" ? createdPatient : {}),
							id: createdPatientId,
							full_name: createdPatientName,
							National_id: createdPatient?.National_id ?? nationalId ?? null,
							Member_id: createdPatient?.Member_id ?? memberId ?? null,
							Patient_passport: createdPatient?.Patient_passport ?? patientPassport ?? null,
						}),
					};

					setPatient((prev) => {
						if (prev.some((p: any) => String(p.value) === String(newOption.value))) {
							return prev;
						}
						return [...prev, newOption];
					});
				}
				setIsPatDialogOpen(false);
				setNationalId(undefined);
				setMemberId(undefined);
				setPatientPassport(undefined);
				setPatientDob("");
				setFirstName("");
				setLastName("");
				setGender("");
				setPolicy(null);
				setTotalCoverage(0);
				setCurrentSpending(0);
				setPstatus(true);
				toast({ title: "Success", description: "Patient Added Successfully!" });
			} else {
				throw new Error(response.data.message || "Failed to add patient");
			}
		} catch (error: any) {
			const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || "Failed to add patient";
			toast({ title: "Error", description: errorMessage, variant: "destructive" });
		}
	};

	const {
		currentPage,
		setCurrentPage,
		pageSize,
		setPageSize,
		totalPages,
		totalCount,
		allowedPageSizes,
		isTableLoading,
		manualPagination,
		fetchItems,
	} = useServerPaginatedList<Result, {
		search: string;
		status: string;
		deleted: ServerDeletedFilterValue;
	}>({
		filters: { ...advancedSearch, deleted: filterDeleted },
		fetchPage: ({ page, limit, filters }) =>
			VipAxiosInstance.get("getVip", {
				params: {
					page,
					limit,
					name: filters.search.trim() || undefined,
					patient: filters.search.trim() || undefined,
					active:
						filters.status === "Active"
							? "true"
							: filters.status === "Inactive"
								? "false"
								: undefined,
					is_deleted: filters.deleted,
				},
			}),
		setRows: (rows) => setItems({ result: rows }),
		normalizeRow: (item: any) => normalizeVipRow(item),
		sortRows: (rows) => [...rows].sort((a: any, b: any) => Number(b.id) - Number(a.id)),
		onError: (error) => {
			console.error("Error fetching items:", error);
			toast({ title: "Error", description: "Failed to fetch VIP lists", variant: "destructive" });
		},
	});

  const visiblePatientKey = items.result.map(row => `${row.id}:${normalizeVipPatients((row as any).patients).map(p => patientIdFromValue(p)).join(",")}`).join("|");
  useEffect(() => {
    const sequence = ++patientLookupRequest.current;
    const linked = ([] as any[]).concat(...items.result.map(row => normalizeVipPatients((row as any).patients)));
    if (!linked.length) return;
    void refreshPatientStatuses(linked).then(() => {
      if (sequence !== patientLookupRequest.current) return;
    });
  }, [visiblePatientKey]);

	const toggleVipStatus = async (item: Result, force = false): Promise<void> => {
		if ((item as any).is_deleted) return;
		try {
			const updatedItem = {
				token,
				force,
				id: item.id,
				Name: item.name,
				patient_ids: normalizeVipPatients((item as any).patients)
					.map((p: any) => patientIdFromValue(p))
					.filter((patientId: number | null): patientId is number => patientId !== null),
				Status: !item.status,
			};
			const response = await VipAxiosInstance.put("updateVip", updatedItem);
			if (response.data.success) {
				await fetchItems();
				setForceDialogOpen(false);
				setForceError("");
				toast({ title: "Success", description: `Status changed to ${!item.status ? "Active" : "Inactive"}.` });
			} else {
				setForceId(item.id);
				setForceAction("status");
				setForceError(response.data.error || "This VIP is used elsewhere.");
				setForceDialogOpen(true);
			}
		} catch (error: any) {
			const errMsg = error.response?.data?.error || error.message || "An unexpected error occurred.";
			if (error.response?.status === 409 || errMsg.toLowerCase().includes("used")) {
				setForceId(item.id);
				setForceAction("status");
				setForceError(errMsg);
				setForceDialogOpen(true);
			} else {
				toast({ title: "Error", description: errMsg, variant: "destructive" });
			}
		}
	};

	const addItem = async (): Promise<void> => {
		const vipNameValidationError = validateStrictName(
			vipListName,
			"VIP List Name is required.",
			"Name must be at least 3 characters long",
		);

		setNameError(vipNameValidationError);
		if (vipNameValidationError) {
			setIsDialogOpen(true);
			return;
		}
		try {
			const newGroupArray = {
				token,
				Name: vipListName,
				patient_ids: getSelectedPatientIds(),
				Status: vipStatus === "active",
			};
			const response = await VipAxiosInstance.post("addNewVip", newGroupArray);
			if (response.data.success) {
				await fetchItems();
				setCurrentPage(0);
				clear();
				setIsDialogOpen(false);
				toast({ title: "Success", description: "New VIP Added Successfully." });
			} else {
				const errors = response.data.errors || [];
				const errorMessages = errors.map((error: any) => error.error).join("\n");
				toast({ title: "Error", description: errorMessages || response.data.message || "Failed to add the group.", variant: "destructive" });
			}
		} catch (error: AxiosError | any) {
			toast({ title: "Error", description: error.response?.data?.error || error.message || "An unexpected error occurred.", variant: "destructive" });
		}
	};

	const handleEdit = (item: Result) => {
		setVipListName(item.name || "");
		setVipStatus(item.status ? "active" : "inactive");
		setPatient(
			normalizeVipPatients((item as any).patients).map((obj: any) => ({
				value: (obj.patient_id ?? obj.id) as any,
				label: formatVipPatientLabel(obj),
                ...obj, isInactive: patientIsInactive(obj), __inactive: patientIsInactive(obj),
                is_active: patientIsInactive(obj) ? false : (obj.is_active ?? obj.Status),
			}))
		);
		const patientId = item.patientcivilid || item.patientpassport || "";
		setPatientIdentifier(patientId);
		if (item.patientcivilid || item.patientpassport) {
			setIsPatientExisting(true);
			setPatientName(item.fullname || "");
			setDob(item.patientdob ? new Date(item.patientdob).toISOString().split("T")[0] : "");
			setGender(item.gender || "");
		} else {
			setIsPatientExisting(false);
			setPatientName("");
			setDob("");
			setGender("");
		}
		setcurrentspending(item.currentspending);
		settotalcoverage(item.totalcoverage);
		setEditIndex(item.id);
	};

	const handleName2Change = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setVipListName(value);
		setNameError(
			validateStrictName(
				value,
				"VIP List Name is required.",
				"Name must be at least 3 characters long",
			),
		);
	};

	const handleNameChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setPatientIdentifier(value);
		try {
			const response = await PatientRelationAxiosInstance.get("getPatient", { params: { patient_identifier: value } });
			const data = response.data?.result || [];
			setPatientItems({ result: data });
		} catch (error) {
			console.error("Error loading patients:", error);
		}
	};

	const removePatientFromVip = async (
		vipId: string | number,
		patientToRemove: any,
	): Promise<void> => {
		try {
            if (patientIsInactive(enrichPatients([patientToRemove])[0])) return;
			const currentVip = items.result.find((item) => item.id === vipId);
			if (!currentVip) return;

			const normalizedPatients = normalizeVipPatients((currentVip as any).patients);
			const removeId = patientIdFromValue(patientToRemove);
			const removeName = String(patientToRemove?.name ?? "");

			if (removeId === null) {
				throw new Error("Patient ID is required to remove a patient from a VIP list.");
			}

			const remainingPatientIds = normalizedPatients
				.filter((candidate: any) => patientIdFromValue(candidate) !== removeId)
				.map((candidate: any) => patientIdFromValue(candidate))
				.filter((patientId: number | null): patientId is number => patientId !== null);

			const updatedItem = {
				token,
				force: false,
				id: vipId,
				Name: currentVip.name,
				patient_ids: remainingPatientIds,
				Status: currentVip.status,
			};

			const response = await VipAxiosInstance.put("updateVip", updatedItem);

			if (response.data.success) {
				await fetchItems();
				toast({
					title: "Success",
					description: `Patient "${removeName || removeId}" removed from VIP list successfully.`,
				});
			}
		} catch (error: any) {
			toast({
				title: "Error",
				description:
					error.response?.data?.error || error.message || "Failed to remove patient.",
				variant: "destructive",
			});
		}
	};

	const removeItem = async (id: string | number): Promise<void> => {
		try {
			const response = await VipAxiosInstance.delete(`softDeleteVip/${id}`, { data: { token } });
			if (response.data.success) {
				await fetchItems();
				toast({ title: "Success", description: "VIP Deleted Successfully." });
			} else {
				setForceId(id);
				setForceAction("softDelete");
				setForceError(response.data.error || "This VIP is used elsewhere.");
				setForceDialogOpen(true);
			}
		} catch (error: any) {
			const errMsg = error.response?.data?.error || "Failed to delete VIP";
			if (error.response?.status === 409 || errMsg.toLowerCase().includes("used")) {
				setForceId(id); setForceAction("softDelete"); setForceError(errMsg); setForceDialogOpen(true);
			} else {
				toast({ title: "Error", description: errMsg, variant: "destructive" });
			}
		}
	};

	const restoreItem = async (id: string | number): Promise<void> => {
		try {
			const response = await VipAxiosInstance.patch(`restoreVip/${id}`, { token });
			if (response.data.success) {
				await fetchItems();
				toast({ title: "Success", description: "VIP restored successfully." });
			} else {
				toast({ title: "Error", description: response.data.error || "Failed to restore VIP", variant: "destructive" });
			}
		} catch (error: any) {
			toast({ title: "Error", description: error.response?.data?.error || "Failed to restore VIP", variant: "destructive" });
		}
	};

	const permanentDeleteItem = async (id: string | number, force = false): Promise<void> => {
		try {
			const url = force ? `deleteVip/${id}?force=true` : `deleteVip/${id}`;
			const response = await VipAxiosInstance.delete(url, { data: { token } });
			if (response.data.success) {
				await fetchItems();
				setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
				toast({ title: "Success", description: "VIP Permanently Deleted." });
			} else {
				setForceAction("hardDelete");
				setForceError(response.data.error || "This VIP is used elsewhere.");
			}
		} catch (error: any) {
			setForceAction("hardDelete");
			setForceError(error.response?.data?.error || "Failed to delete VIP");
		}
	};

	const forceSoftDelete = async (id: string | number): Promise<void> => {
		try {
			const response = await VipAxiosInstance.delete(`softDeleteVip/${id}?force=true`, { data: { token } });
			if (response.data.success) {
				await fetchItems();
				setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
				toast({ title: "Success", description: "VIP force Deleted Successfully." });
			} else {
				setForceError(response.data.error || "Failed to force delete VIP");
			}
		} catch (error: any) {
			setForceError(error.response?.data?.error || "Failed to force delete VIP");
		}
	};

	const updateItem = async (id: string | number): Promise<void> => {
		const vipNameValidationError = validateStrictName(
			vipListName,
			"VIP List Name is required.",
			"Name must be at least 3 characters long",
		);

		setNameError(vipNameValidationError);
		if (vipNameValidationError) return;

		const patientIds = getSelectedPatientIds();

		try {
			const updatedItem = {
				token,
				force: false,
				id,
				Name: vipListName,
				patient_ids: patientIds,
				Status: vipStatus === "active",
			};

			const response = await VipAxiosInstance.put("updateVip", updatedItem);
			if (response.data.success) {
				await fetchItems();
				clear();
				setIsDialogOpen(false);
				toast({ title: "Success", description: "VIP updated successfully." });
			} else {
				const errors = response.data.errors || [];
				const errorMessages = errors.map((error: any) => error.error).join("\n");
				toast({ title: "Error", description: errorMessages || response.data.message || "Failed to update the VIP.", variant: "destructive" });
			}
		} catch (error: AxiosError | any) {
			toast({ title: "Error", description: error.response?.data?.error || error.message || "An unexpected error occurred.", variant: "destructive" });
		}
	};

	const columns: ColumnDef<Result>[] = [
		{
			accessorKey: "id",
			header: ({ column }) => (
				<Button variant="ghost" className="font-bold text-white w-full justify-center hover:bg-white/10 hover:text-white" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
					ID{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
				</Button>
			),
			cell: ({ row }) => <span className="block text-center">{currentPage * pageSize + row.index + 1}</span>,
		},
		{
			accessorKey: "name",
			header: ({ column }) => (
				<Button variant="ghost" className="font-bold text-white w-full justify-center hover:bg-white/10 hover:text-white" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
					List Name{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
				</Button>
			),
			cell: ({ row }) => {
				const isDeleted = (row.original as any).is_deleted;
				return (
					<span className={`block text-center ${isDeleted ? "line-through text-gray-400" : ""}`}>
						{row.getValue("name")}
					</span>
				);
			},
		},
		{
			accessorKey: "patients",
			header: ({ column }) => (
				<Button variant="ghost" className="font-bold text-white w-full justify-center hover:bg-white/10 hover:text-white" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
					Patients{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
				</Button>
			),
			cell: ({ row }) => {
				const patients = enrichPatients(row.getValue("patients"));
				const vipId = row.original.id;

				if (!patients || !Array.isArray(patients) || patients.length === 0) {
					return <span className="text-gray-400 text-center block">-</span>;
				}

				const searchTerm = advancedSearch.search.toLowerCase().trim();
				const hasSearch = searchTerm.length >= 2;
				const matchingPatients = hasSearch
					? patients.filter((patient) =>
							patient.name?.toLowerCase().includes(searchTerm),
						)
					: [];

				const hasMatches =
					hasSearch && matchingPatients.length > 0;
				const displaySource = hasMatches
					? matchingPatients
					: patients;
				const displayPatients = displaySource.slice(0, 2);
				const remainingCount = Math.max(
					0,
					displaySource.length - displayPatients.length,
				);

				return (
					<div className="flex flex-col items-center gap-1">
						<div className="flex flex-wrap justify-center items-center gap-1.5">
							{displayPatients.map((patient, idx) => {
								const isMatching =
									hasSearch &&
									patient.name?.toLowerCase().includes(searchTerm);

								return (
									<div
										key={`${patient.patient_id ?? patient.id ?? patient.name}-${idx}`}
										className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 transition-all ${
											patientIsInactive(patient) ? "bg-gray-200 border-gray-300 text-slate-600" : isMatching
												? "bg-yellow-200 border-yellow-400 text-yellow-900"
												: "bg-purple-50 border-purple-200 text-purple-800"
										}`}
									>
										<span className="text-xs font-medium">
											{patient.name}{patientIsInactive(patient) ? " · Inactive" : ""}
										</span>
										{!patientIsInactive(patient) && <button
											onClick={(event) => {
												event.stopPropagation();
												if (
													window.confirm(
														`Remove "${patient.name}" from this VIP list?`,
													)
												) {
													removePatientFromVip(vipId, patient);
												}
											}}
											className="text-red-500 hover:text-red-700 hover:bg-red-100 rounded-full w-5 h-5 flex items-center justify-center transition-all"
											title="Remove from VIP list"
										>
											<i className="fa-solid fa-times text-xs"></i>
										</button>}
									</div>
								);
							})}

							{remainingCount > 0 && (
								<button
									onClick={(event) => {
										event.stopPropagation();
										const dialogPatients = hasMatches
											? matchingPatients
											: patients;
										setSelectedPatients(dialogPatients);
										handleOpenPatientDialog(dialogPatients);
									}}
									className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold text-white transition-colors ${
										hasMatches
											? "bg-yellow-600 hover:bg-yellow-700"
											: "bg-purple-600 hover:bg-purple-700"
									}`}
								>
									+{remainingCount} more
								</button>
							)}
						</div>

						<span className="text-[10px] text-gray-400">
							{hasMatches
								? `${matchingPatients.length}/${patients.length}`
								: `${patients.length} total`}
						</span>
					</div>
				);
			},
		},
		{
			accessorKey: "status",
			header: ({ column }) => (
				<Button variant="ghost" className="font-bold text-white w-full justify-center hover:bg-white/10 hover:text-white" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
					Status{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
				</Button>
			),
			cell: ({ row }) => {
				const item = row.original;
				const isDeleted = (item as any).is_deleted;
				const statusValueStr = item.status ? "active" : "inactive";
				return (
					<div className="flex justify-center">
						<button
							data-testid={`vip-table-btn-status-toggle-${item.id}`}
							data-row-id={item.id}
							data-status={statusValueStr}
							onClick={() => !isDeleted && toggleVipStatus(item)}
							disabled={!!isDeleted}
							className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isDeleted ? "opacity-40 cursor-not-allowed bg-gray-200" : item.status ? "bg-green-500" : "bg-gray-300"}`}
						>
							<span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${item.status && !isDeleted ? "translate-x-6" : "translate-x-1"}`} />
						</button>
					</div>
				);
			},
		},
		{
			id: "view",
			header: () => (
				<Button variant="ghost" className="font-bold text-white w-full justify-center hover:bg-white/10 hover:text-white">
					View
				</Button>
			),
			cell: ({ row }) => {
				const item = row.original;
				return (
					<div className="flex justify-center items-center">
						<button
							data-testid={`vip-table-btn-view-details-${item.id}`}
							onClick={() => void handleOpenDialog(item)}
							className="text-blue-600 hover:text-blue-800"
							title="View Details"
						>
							<i className="fa-solid fa-eye text-lg"></i>
						</button>
					</div>
				);
			},
		},
	];

	return (
<div className="h5-container mx-auto px-1 pt-16 pb-4" data-qa-page="vip">
				<div className="flex justify-between items-center">
				<h5 className="mb-6 flex items-center gap-2 font-bold text-2xl mt-4 text-blue-900" data-testid="vip-page-title">
					<span className="icon-container flex items-center justify-center w-10 h-10 rounded-full bg-blue-900 text-white">
						<i className="fa-solid fa-crown"></i>
					</span>
					VIP Patient
				</h5>
			</div>

			<DataTable2
        qaIdPrefix="vip-data-table"
				columns={columns}
				data={items.result}
				searchValue={advancedSearch.search}
				onSearchChange={(value) => {
					setAdvancedSearch((previous) => ({ ...previous, search: value }));
					setCurrentPage(0);
				}}
				searchPlaceholder="Search..."
				searchHelpText="VIP List Name, or Patient Name"
				addButtonLabel="Add New VIP"
				showAddButton={true}
		compactToolbar={true}
		toolbarUpload={<FileUpload fetchItems={fetchItems} />}
				onEdit={handleEdit}
				onEditClick={handleEditClick}
				onDownload={handleDownload}
				onDelete={(id) => {
					const item = items.result.find(i => i.id === id);
					if ((item as any)?.is_deleted) {
						setForceId(id);
						setForceAction("hardDelete");
						setForceError("");
						setForceDialogOpen(true);
					} else {
						removeItem(id);
					}
				}}
				onRestore={canManageDeleted ? restoreItem : undefined}
				canHardDelete={canManageDeleted}
				pageIndex={currentPage}
				onPageChange={setCurrentPage}
				manualPagination={manualPagination}
				pageCount={totalPages}
				totalCount={totalCount}
				pageSize={pageSize}
				onPageSizeChange={(nextPageSize) => {
					setPageSize(nextPageSize);
					setCurrentPage(0);
				}}
				allowedPageSizes={allowedPageSizes}
				isLoading={isTableLoading}
				onAdd={handleAddClick}
				isRowDeleted={(row: any) => row.is_deleted === true}
				toolbarExtras={
					<div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
						<ServerStatusPills
							value={
								advancedSearch.status === "Active"
									? "true"
									: advancedSearch.status === "Inactive"
										? "false"
										: ""
							}
							onChange={(value) => {
								setAdvancedSearch((previous) => ({
									...previous,
									status: value === "true" ? "Active" : value === "false" ? "Inactive" : "",
								}));
								setCurrentPage(0);
							}}
						/>
						<ServerDeletedPills
							value={filterDeleted}
							onChange={(value) => {
								setFilterDeleted(value);
								setCurrentPage(0);
							}}
						/>
					</div>
				}
			/>

		{/* ─── View Details Dialog ─────────────────────────────────────────── */}
{selectedItem && (
  <Dialog open={!!selectedItem} onOpenChange={(open) => { if (!open) setSelectedItem(null); }}>
	<DialogContent
		data-testid="vip-dialog-view-details"
		className="max-w-[780px] max-h-[92vh] flex flex-col p-0 overflow-hidden bg-white border-none rounded-2xl"
		style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)" }}
	>

	  {/* Header */}
	  <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)", padding: "24px 28px 20px", flexShrink: 0, position: "relative", overflow: "hidden" }}>
		<div style={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
		<div style={{ position: "absolute", bottom: -30, left: -20, width: 100, height: 100, borderRadius: "50%", background: "rgba(255,255,255,0.03)" }} />
		<div style={{ display: "flex", alignItems: "flex-start", gap: 16, position: "relative", zIndex: 1 }}>
		  <div style={{ width: 48, height: 48, borderRadius: 14, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, color: "#fff", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)" }}>
			<i className="fa-solid fa-crown" />
		  </div>
		  <div style={{ flex: 1, minWidth: 0 }}>
			<div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 2, flexWrap: "wrap" }}>
			  <h2 style={{ fontSize: 20, fontWeight: 700, color: "#fff", margin: 0, letterSpacing: "-0.01em" }}>{selectedItem.name}</h2>
			  <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, flexShrink: 0, background: selectedItem.status ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.15)", color: selectedItem.status ? "#86efac" : "#fca5a5", border: `1px solid ${selectedItem.status ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.2)"}` }}>
				<span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} />
				{selectedItem.status ? "Active" : "Inactive"}
			  </span>
			  {(selectedItem as any).is_deleted && (
				<span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, background: "rgba(239,68,68,0.15)", color: "#fca5a5", border: "1px solid rgba(239,68,68,0.2)" }}>
				  <i className="fa-solid fa-trash" style={{ fontSize: 10 }} /> Deleted
				</span>
			  )}
			</div>
			<p style={{ fontSize: 12, color: "rgba(147,197,253,0.8)", margin: 0 }}>Vip ID: <span style={{ fontWeight: 600, color: "#93c5fd" }}>#{selectedItem.id}</span></p>
		  </div>
		</div>
	  </div>

	  {/* Body */}
	  <ScrollArea className="flex-1" style={{ background: "#f8fafc" }}>
		<div style={{ padding: "20px 28px 16px" }}>

		  {/* Basic Info */}
		  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
			<div style={{ width: 28, height: 28, borderRadius: 8, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #dbeafe" }}>
			  <i className="fa-solid fa-circle-info" style={{ fontSize: 12, color: "#3b82f6" }} />
			</div>
			<span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Basic information</span>
		  </div>
		  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
			{[
			  { label: "List name", value: selectedItem.name, icon: "fa-crown", color: "#f59e0b" },
			  { label: "Status", value: selectedItem.status ? "Active" : "Inactive", icon: "fa-toggle-on", color: "#10b981" },
			].map((field) => (
			  <div key={field.label} style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
				<div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
				  <div style={{ width: 22, height: 22, borderRadius: 6, background: `${field.color}12`, display: "flex", alignItems: "center", justifyContent: "center" }}>
					<i className={`fa-solid ${field.icon}`} style={{ fontSize: 10, color: field.color }} />
				  </div>
				  <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "capitalize", letterSpacing: "0.05em" }}>{field.label}</span>
				</div>
				<span style={{ fontSize: 14, fontWeight: 600, color: "#1e293b" }}>{field.value || "—"}</span>
			  </div>
			))}
		  </div>

		  <div style={{ height: 1, background: "#e2e8f0", margin: "0 0 20px" }} />

		  {/* Patients */}
		  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
			<div style={{ width: 28, height: 28, borderRadius: 8, background: "#faf5ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #e9d5ff" }}>
			  <i className="fa-solid fa-users" style={{ fontSize: 12, color: "#9333ea" }} />
			</div>
			<span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Associated patients</span>
			<span style={{ fontSize: 10, padding: "2px 10px", borderRadius: 9999, background: "#9333ea", color: "#fff", fontWeight: 700 }}>
			  {selectedItem.patients?.length || 0}
			</span>
		  </div>
		  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", marginBottom: 20, boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
			{selectedItem.patients && selectedItem.patients.length > 0 ? (
			  <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 200, overflowY: "auto" }}>
				{selectedItem.patients.map((p, idx) => (
				  <div key={idx} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: patientIsInactive(p) ? "#e5e7eb" : "#f8fafc", borderRadius: 8, border: `1px solid ${patientIsInactive(p) ? "#cbd5e1" : "#e2e8f0"}` }}>
					<div style={{ width: 32, height: 32, borderRadius: "50%", background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, color: "#2563eb", flexShrink: 0, fontSize: 13 }}>
					  {p.name?.charAt(0)}
					</div>
					<span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b", flex: 1 }}>{p.name}{patientIsInactive(p) ? " · Inactive" : ""}</span>
					<span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 9999, background: patientIsInactive(p) ? "#cbd5e1" : "#faf5ff", color: patientIsInactive(p) ? "#334155" : "#7e22ce", border: "1px solid #e2e8f0", fontWeight: 500 }}>{patientIsInactive(p) ? "Inactive" : "Patient"}</span>
				  </div>
				))}
			  </div>
			) : (
			  <span style={{ color: "#94a3b8", fontSize: 12, fontStyle: "italic" }}>No patients linked to this VIP list</span>
			)}
		  </div>

		  <div style={{ height: 1, background: "#e2e8f0", margin: "0 0 20px" }} />

		  {/* Audit Trail */}
		  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
			<div style={{ width: 28, height: 28, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
			  <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: 12, color: "#16a34a" }} />
			</div>
			<span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Audit trail</span>
		  </div>
		  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
			<div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
			  <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Created by</span>
			  <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
				<div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #3b82f6, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
				  {String(resolveUserName((selectedItem as any).created_by) ?? "?")[0].toUpperCase()}
				</div>
				<span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{resolveUserName((selectedItem as any).created_by)}</span>
			  </div>
			</div>
			<div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
			  <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Created on</span>
			  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
				<i className="fa-regular fa-calendar" style={{ fontSize: 14, color: "#3b82f6" }} />
				<span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
				  {(selectedItem as any).createdat ? new Date((selectedItem as any).createdat).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
				</span>
			  </div>
			</div>
			<div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
			  <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Last updated</span>
			  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
				<i className="fa-regular fa-clock" style={{ fontSize: 14, color: "#10b981" }} />
				<span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
				  {(selectedItem as any).updatedat ? new Date((selectedItem as any).updatedat).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
				</span>
			  </div>
			</div>
		  </div>

	   {Array.isArray((selectedItem as any).updated_by) && (selectedItem as any).updated_by.length > 0 && (
  <div style={{ gridColumn: "1 / -1", background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", marginTop: 12 }}>
	<div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
	  <i className="fa-solid fa-pen-to-square" style={{ fontSize: 11, color: "#10b981" }} />
	  <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.02em" }}>Update History</span>
	  <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#f0fdf4", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0", marginLeft: "auto" }}>
		{(selectedItem as any).updated_by.length} update{(selectedItem as any).updated_by.length !== 1 ? "s" : ""}
	  </span>
	</div>
	<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
	  {((selectedItem as any).updated_by as { id?: number; user_id?: number; at?: string; date?: string; [key: string]: any }[]).slice().reverse().slice(0, showAllUpdates ? undefined : 4).map((entry, i) => {
		const updateDate = entry.date || entry.at || (entry as any).updatedat || (entry as any).timestamp || (i === 0 ? (selectedItem as any).updatedat : undefined);
		return (
		<div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: i === 0 ? "#f0fdf4" : "#f8fafc", border: `1px solid ${i === 0 ? "#bbf7d0" : "#e2e8f0"}` }}>
		  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
			<div style={{ width: 28, height: 28, borderRadius: "50%", background: i === 0 ? "linear-gradient(135deg, #10b981, #059669)" : "linear-gradient(135deg, #94a3b8, #64748b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
			  {String(resolveUserName(entry.id ?? entry.user_id) ?? "?")[0].toUpperCase()}
			</div>
			<span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{resolveUserName(entry.id ?? entry.user_id)}</span>
			{i === 0 && <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#dcfce7", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0" }}>Latest</span>}
		  </div>
		  <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
			{updateDate ? new Date(updateDate).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
		  </span>
		</div>
	  );})}
	  {(selectedItem as any).updated_by.length > 4 && (
		<div style={{ textAlign: "center", padding: "6px 0" }}>
		  <button onClick={() => setShowAllUpdates(v => !v)} style={{ fontSize: 11, color: "#3b82f6", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}>
			{showAllUpdates ? "Show less ↑" : `+${(selectedItem as any).updated_by.length - 4} more updates ↓`}
		  </button>
		</div>
	  )}
	</div>
  </div>
)}

		  <div style={{ paddingBottom: 8 }} />
		</div>
	  </ScrollArea>

	  {/* Footer */}
	  <div style={{ padding: "14px 28px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", alignItems: "center", justifyContent: "flex-end", flexShrink: 0 }}>
		<Button data-testid="vip-btn-close-details" onClick={() => setSelectedItem(null)} style={{ background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "#fff", fontWeight: 600, padding: "8px 28px", borderRadius: 10, boxShadow: "0 2px 8px rgba(37,99,235,0.3)" }}>
		  Close Details
		</Button>
	  </div>
	</DialogContent>
  </Dialog>
)}

			{/* Patient List Dialog */}
			<dialog ref={patientDialogRef} className="patient-list-dialog">
				<div className="dialog-content">
					<button className="dialog-close-icon" onClick={handleClosePatientDialog}>×</button>
					<h2 className="dialog-title flex items-center gap-2 text-xl font-bold text-purple-900">
						<i className="fa-solid fa-users"></i>
						All Patients
					</h2>
					<div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-lg p-4 border border-purple-200 mt-4">
						<div className="grid grid-cols-1 gap-2 max-h-96 overflow-y-auto pr-2" style={{ scrollbarWidth: "thin", scrollbarColor: "#9333ea #e9d5ff" }}>
							{selectedPatients.map((patient, index) => {
								const searchTerm = advancedSearch.search.toLowerCase().trim();
								const isMatching = searchTerm.length > 0 && patient.name?.toLowerCase().includes(searchTerm);
								return (
									<div key={index} className={`flex items-center gap-3 p-3 rounded-lg shadow-sm border transition-all ${patientIsInactive(patient) ? 'bg-gray-200 border-gray-300' : isMatching ? 'bg-yellow-50 border-yellow-400 hover:border-yellow-500 hover:shadow-lg' : 'bg-white border-purple-100 hover:border-purple-300 hover:shadow-md'}`}>
										<div className={`flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0 ${isMatching ? 'bg-yellow-600' : 'bg-purple-600'} text-white`}>
											<i className="fa-solid fa-user text-sm"></i>
										</div>
										<p className={`font-medium flex-1 ${isMatching ? 'text-yellow-900 font-bold' : 'text-gray-800'}`}>
											{isMatching && <i className="fa-solid fa-star text-yellow-600 mr-2"></i>}
											{patient.name}{patientIsInactive(patient) ? " · Inactive" : ""}
										</p>
										{!patientIsInactive(patient) && <button
											onClick={(e) => {
												e.stopPropagation();
												if (window.confirm(`Remove "${patient.name}" from this VIP list?`)) {
													const vipId = items.result.find(vip => vip.patients?.some(p => p.name === patient.name))?.id;
													if (vipId) { removePatientFromVip(vipId, patient); handleClosePatientDialog(); }
												}
											}}
											className="text-red-500 hover:text-red-700 hover:bg-red-100 rounded-full w-8 h-8 flex items-center justify-center transition-all"
											title="Remove from VIP list"
										>
											<i className="fa-solid fa-trash-alt text-sm"></i>
										</button>}
									</div>
								);
							})}
						</div>
						<div className="mt-4 pt-3 border-t border-purple-200">
							<p className="text-sm text-gray-600 flex items-center gap-2">
								<i className="fa-solid fa-info-circle text-purple-600"></i>
								<span>Total: <strong className="text-purple-900">{selectedPatients.length}</strong> patients</span>
							</p>
						</div>
					</div>
					<button onClick={handleClosePatientDialog} className="w-full mt-4 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 rounded-lg transition-all">
						<i className="fa-solid fa-check mr-2"></i>
						Close
					</button>
				</div>
				<style>{`
		  .patient-list-dialog { border: none; border-radius: 20px; padding: 0; width: 600px; max-width: 90%; box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3); background-color: #fff; }
		  .patient-list-dialog .dialog-content { position: relative; padding: 2rem; }
		  .patient-list-dialog .dialog-close-icon { position: absolute; top: 1rem; right: 1rem; background: #9333ea; color: white; border: none; border-radius: 50%; width: 32px; height: 32px; font-size: 1.3rem; cursor: pointer; transition: all 0.3s ease; display: flex; align-items: center; justify-content: center; }
		  .patient-list-dialog .dialog-close-icon:hover { background: #7e22ce; transform: scale(1.1); }
		`}</style>
			</dialog>

			{/* Add / Edit Dialog */}
<Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
<DialogContent
  data-testid={editIndex === null ? "vip-dialog-add-vip" : "vip-dialog-edit-vip"}
  className="max-w-[600px] max-h-[90vh] flex flex-col p-0 bg-white border-none rounded-2xl"
  onInteractOutside={(event: any) => {
	const target = event.target as HTMLElement;

	if (
	  target.closest(".vip-patient-select__menu") ||
	  target.closest(".vip-patient-select__menu-portal")
	) {
	  event.preventDefault();
	}
  }}
  onPointerDownOutside={(event: any) => {
	const target = event.target as HTMLElement;

	if (
	  target.closest(".vip-patient-select__menu") ||
	  target.closest(".vip-patient-select__menu-portal")
	) {
	  event.preventDefault();
	}
  }}
  style={{
	boxShadow:
	  "0 25px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)",
	overflow: "visible",
  }}
>
		<div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)", padding: "22px 28px", position: "relative", overflow: "hidden", borderRadius: "16px 16px 0 0", flexShrink: 0 }}>
	  <div style={{ position: "absolute", top: -40, right: -40, width: 140, height: 140, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
	  <div style={{ display: "flex", alignItems: "center", gap: 14, position: "relative", zIndex: 1 }}>
		<div style={{ width: 42, height: 42, borderRadius: 12, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, color: "#fff", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)" }}>
		  <i className={`fa-solid ${editIndex === null ? "fa-plus" : "fa-pen"}`} />
		</div>
		<div>
		  <h2 style={{ fontSize: 18, fontWeight: 700, color: "#fff", margin: 0 }}>{editIndex === null ? "Add VIP" : "Edit VIP"}</h2>
		  </div>
	  </div>
	</div>

	<ScrollArea className="flex-1" style={{ background: "#f8fafc" }}>
	  <div style={{ padding: "22px 28px 32px" }}>
		<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
		  <div style={{ width: 26, height: 26, borderRadius: 7, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #dbeafe" }}>
			<i className="fa-solid fa-circle-info" style={{ fontSize: 11, color: "#3b82f6" }} />
		  </div>
		  <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Basic Information</span>
		</div>
		<div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", marginBottom: 18 }}>
		  <Label htmlFor="vip_name" className="text-xs font-semibold text-slate-500">VIP List Name <span className="text-orange-500">*</span></Label>
		  <Input
			data-testid="vip-input-list-name"
			id="vip_name"
			value={vipListName}
			onChange={handleName2Change}
			placeholder="Enter VIP List Name"
			className={`mt-1.5 ${nameError ? "border-red-400 focus-visible:ring-red-300" : ""}`}
		  />
		  {nameError && <p data-testid="vip-message-error-list-name" className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{nameError}</p>}

		  <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #e2e8f0" }}>
			<Label className="text-xs font-semibold text-slate-500">Status</Label>
			<div style={{ display: "flex", gap: 8, marginTop: 8, maxWidth: 260 }}>
			  <button
				data-testid="vip-btn-vip-status-active"
				type="button"
				onClick={() => setVipStatus("active")}
				style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 0", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", border: vipStatus === "active" ? "1px solid #16a34a" : "1px solid #e2e8f0", background: vipStatus === "active" ? "#f0fdf4" : "#fff", color: vipStatus === "active" ? "#15803d" : "#64748b" }}
			  >
				<span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} /> Active
			  </button>
			  <button type="button" onClick={() => setVipStatus("inactive")} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 0", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", border: vipStatus === "inactive" ? "1px solid #dc2626" : "1px solid #e2e8f0", background: vipStatus === "inactive" ? "#fef2f2" : "#fff", color: vipStatus === "inactive" ? "#b91c1c" : "#64748b" }}>
				<span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} /> Inactive
			  </button>
			</div>
		  </div>
		</div>

		{editIndex !== null && (
		  <>
			<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
			  <div style={{ width: 26, height: 26, borderRadius: 7, background: "#faf5ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #e9d5ff" }}>
				<i className="fa-solid fa-users" style={{ fontSize: 11, color: "#9333ea" }} />
			  </div>
			  <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Patients</span>
			</div>
			<div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
			  <Label className="text-xs font-semibold text-slate-500">Assigned Patients</Label>
			  <div data-testid="vip-select-assigned-patients-wrapper" style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
				<div style={{ flex: 1 }}><VipPatientSelector onChange={handlePatientChange} clear={clearPatient} value={patient} /></div>
				<button data-testid="vip-btn-open-add-patient-dialog" type="button" onClick={() => { setIsPatDialogOpen(true); clear2(); }} style={{ padding: "8px 14px", borderRadius: 8, background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
				  <i className="fa-solid fa-plus mr-1" /> New
				</button>
			  </div>
			</div>
		  </>
		)}
	  </div>
	</ScrollArea>

	<div style={{ padding: "16px 28px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", justifyContent: "flex-end", gap: 10, borderRadius: "0 0 16px 16px", flexShrink: 0 }}>
	  <Button data-testid="vip-btn-cancel-vip" variant="outline" onClick={() => { setEditIndex(null); clear(); setIsDialogOpen(false); }} style={{ borderRadius: 10, fontWeight: 600 }}>Cancel</Button>
	  <Button
		data-testid={editIndex === null ? "vip-btn-add-vip-submit" : "vip-btn-update-vip-submit"}
		disabled={Boolean(
			validateStrictName(
				vipListName,
				"VIP List Name is required.",
				"Name must be at least 3 characters long",
			),
		)}
		onClick={async () => { if (editIndex === null) await addItem(); else await updateItem(editIndex); }}
		style={{ background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "#fff", fontWeight: 600, padding: "8px 24px", borderRadius: 10, boxShadow: "0 2px 8px rgba(37,99,235,0.3)" }}
	  >
		<i className={`fa-solid ${editIndex === null ? "fa-plus" : "fa-check"} mr-2`}></i>
		{editIndex === null ? "Add VIP" : "Update VIP"}
	  </Button>
	</div>
  </DialogContent>
</Dialog>

			{/* ─── Force / Hard Delete Dialog ──────────────────────────────────── */}
			{forceDialogOpen && (
				<div style={{ position: "fixed", inset: 0, background: "#00000088", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" }}>
					<div style={{ background: "#fff", borderRadius: 16, padding: 32, width: 440, boxShadow: "0 20px 50px rgba(0,0,0,0.3)", border: "1px solid #fecaca" }}>
						<div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 12 }}>
							<div style={{ width: 60, height: 60, borderRadius: "50%", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center" }}>
								<i className="fa-solid fa-triangle-exclamation" style={{ fontSize: 28, color: "#dc2626" }}></i>
							</div>

							{forceAction === "hardDelete" && (
								<>
									<h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>Permanent Delete</h3>
									<p style={{ fontSize: 13, color: "#64748b", margin: 0, lineHeight: 1.6 }}>
										This will <strong style={{ color: "#dc2626" }}>permanently delete</strong> the VIP.<br />This action <strong>cannot be undone</strong>.
									</p>
									{forceError && (
										<div style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
											<div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
												<i className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
												<p style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
											</div>
										</div>
									)}
									<div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
										<button
											onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }}
											style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
											Cancel
										</button>
										<button
											onClick={() => forceId !== null && permanentDeleteItem(forceId, true)}
											style={{ flex: 1, background: "#7f1d1d", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
											<i className="fa-solid fa-bolt mr-2"></i>Force Delete Anyway
										</button>
									</div>
								</>
							)}

							{(forceAction === "softDelete" || forceAction === "status") && (
								<>
									<h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>
										{forceAction === "status" ? "Status Change Blocked" : "VIP Is In Use"}
									</h3>
									<div style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
										<div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
											<i className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
											<p style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
										</div>
									</div>
									<p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
										Do you want to <strong style={{ color: "#dc2626" }}>force {forceAction === "status" ? "update status" : "delete"}</strong> anyway?
									</p>
									<div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
										<button
											onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }}
											style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
											Cancel
										</button>
										<button
											onClick={() => {
												if (!forceId) return;
												if (forceAction === "softDelete") forceSoftDelete(forceId);
												else if (forceAction === "status") {
													const item = items.result.find(i => i.id === forceId);
													if (item) toggleVipStatus(item, true);
												}
											}}
											style={{ flex: 1, background: "#dc2626", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
											<i className="fa-solid fa-bolt mr-2"></i>Force {forceAction === "status" ? "Update" : "Delete"}
										</button>
									</div>
								</>
							)}
						</div>
					</div>
				</div>
			)}

			<Toaster />

			{/* Add Patient Dialog */}
<Dialog
  open={isPatDialogOpen}
  onOpenChange={(open) => {
    setIsPatDialogOpen(open);
    if (!open) clear2();
  }}
>
  <DialogContent
    data-testid="vip-dialog-add-patient-inside-vip"
    className="max-w-[820px] max-h-[90vh] flex flex-col p-0 bg-white border-none rounded-2xl"
    style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)", overflow: "visible" }}
  >
	<div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)", padding: "22px 28px", position: "relative", overflow: "hidden", borderRadius: "16px 16px 0 0", flexShrink: 0 }}>
	  <div style={{ position: "absolute", top: -40, right: -40, width: 140, height: 140, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
	  <div style={{ display: "flex", alignItems: "center", gap: 14, position: "relative", zIndex: 1 }}>
		<div style={{ width: 42, height: 42, borderRadius: 12, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, color: "#fff", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)" }}>
		  <i className="fa-solid fa-user-plus" />
		</div>
		<div>
		  <h2 style={{ fontSize: 18, fontWeight: 700, color: "#fff", margin: 0 }}>Add Patient</h2>
		</div>
	  </div>
	</div>

	<ScrollArea className="flex-1" style={{ background: "#f8fafc" }}>
	  <div style={{ padding: "22px 28px 32px" }}>
		<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
		  <div style={{ width: 26, height: 26, borderRadius: 7, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #dbeafe" }}>
			<i className="fa-solid fa-user" style={{ fontSize: 11, color: "#3b82f6" }} />
		  </div>
		  <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Personal Information</span>
		</div>
		<div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", marginBottom: 18 }}>
		  <div className="grid grid-cols-2 gap-4">
			<div>
			  <Label className="text-xs font-semibold text-slate-500">First Name <span className="text-orange-500">*</span></Label>
			  <Input
				data-testid="vip-input-patient-first-name"
				value={firstName}
				onChange={handleFirstNameChange}
				placeholder="Enter First Name"
				className={`mt-1.5 ${firstError ? "border-red-400 focus-visible:ring-red-300" : ""}`}
			  />
			  {firstError && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{firstError}</p>}
			</div>
			<div>
			  <Label className="text-xs font-semibold text-slate-500">Last Name <span className="text-orange-500">*</span></Label>
			  <Input
				data-testid="vip-input-patient-last-name"
				value={lastName}
				onChange={handleLastNameChange}
				placeholder="Enter Last Name"
				className={`mt-1.5 ${lastError ? "border-red-400 focus-visible:ring-red-300" : ""}`}
			  />
			  {lastError && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{lastError}</p>}
			</div>
			<div>
			  <Label className="text-xs font-semibold text-slate-500">Date of Birth <span className="text-orange-500">*</span></Label>
			  <Input data-testid="vip-input-patient-dob" type="date" value={patientDob} onChange={handleDobChange} className="mt-1.5" />
			  {dobError && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{dobError}</p>}
			</div>
			<div>
			  <Label className="text-xs font-semibold text-slate-500">Gender <span className="text-orange-500">*</span></Label>
			  <div data-testid="vip-select-patient-gender-wrapper" style={{ display: "flex", gap: 8, marginTop: 8 }}>
				<button data-testid="vip-btn-patient-gender-male" type="button" onClick={() => setGender("male")} style={{ flex: 1, padding: "8px 0", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", border: gender === "male" ? "1px solid #2563eb" : "1px solid #e2e8f0", background: gender === "male" ? "#eff6ff" : "#fff", color: gender === "male" ? "#1d4ed8" : "#64748b" }}>Male</button>
				<button data-testid="vip-btn-patient-gender-female" type="button" onClick={() => setGender("female")} style={{ flex: 1, padding: "8px 0", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", border: gender === "female" ? "1px solid #ec4899" : "1px solid #e2e8f0", background: gender === "female" ? "#fdf2f8" : "#fff", color: gender === "female" ? "#be185d" : "#64748b" }}>Female</button>
			  </div>
			  {genderError && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{genderError}</p>}
			</div>
		  </div>
		</div>

		<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
		  <div style={{ width: 26, height: 26, borderRadius: 7, background: "#faf5ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #e9d5ff" }}>
			<i className="fa-solid fa-id-card" style={{ fontSize: 11, color: "#9333ea" }} />
		  </div>
		  <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Identifiers</span>
		</div>
		<div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", marginBottom: 18 }}>
		  <p className="mb-3 text-xs text-slate-500">
			Enter at least one identifier <span className="text-orange-500">*</span>. You may provide National ID, Member ID, Passport, or any combination of them.
		  </p>
		  <div className="grid grid-cols-3 gap-4">
			<div>
			  <Label className="text-xs font-semibold text-slate-500">National ID</Label>
			  <Input data-testid="vip-input-patient-national-id" type="text" inputMode="numeric" placeholder="Enter National ID" value={nationalId?.toString() ?? ""} onChange={handleIDChange} className={`mt-1.5 ${identifierError || identifierGroupError ? "border-red-400 focus-visible:ring-red-300" : ""}`} />
			  {identifierError && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{identifierError}</p>}
			</div>
			<div>
			  <Label className="text-xs font-semibold text-slate-500">Member ID</Label>
			  <Input data-testid="vip-input-patient-member-id" type="text" inputMode="numeric" placeholder="Enter Member ID" value={memberId?.toString() ?? ""} onChange={handleID2Change} className={`mt-1.5 ${midError || identifierGroupError ? "border-red-400 focus-visible:ring-red-300" : ""}`} />
			  {midError && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{midError}</p>}
			</div>
			<div>
			  <Label className="text-xs font-semibold text-slate-500">Passport</Label>
			  <Input data-testid="vip-input-patient-passport" value={patientPassport ?? ""} onChange={handleID3Change} placeholder="Enter Passport Number" className={`mt-1.5 ${ppError || identifierGroupError ? "border-red-400 focus-visible:ring-red-300" : ""}`} />
			  {ppError && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{ppError}</p>}
			</div>
		  </div>
          {checkingPatientIdentifiers && <p className="text-xs text-slate-500 mt-2">Checking patient identifiers...</p>}
          {patientLookupError && <p className="text-red-500 text-xs mt-2" data-testid="vip-patient-lookup-error">{patientLookupError}</p>}
		  {identifierGroupError && (
			<p data-testid="vip-message-error-patient-identifiers" className="text-red-500 text-xs mt-3 flex items-center gap-1">
			  <i className="fa-solid fa-circle-exclamation text-[10px]" />
			  {identifierGroupError}
			</p>
		  )}
		</div>

		<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
		  <div style={{ width: 26, height: 26, borderRadius: 7, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
			<i className="fa-solid fa-file-shield" style={{ fontSize: 11, color: "#16a34a" }} />
		  </div>
		  <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Coverage & Status</span>
		</div>
		<div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
		  <Label className="text-xs font-semibold text-slate-500">Policy (Single Selection)</Label>
		  <div data-testid="vip-select-patient-policy-wrapper" className="mt-1.5"><PolicyNames onChange={handlePolicyChange} defaultValue={policy ? [policy] : []} clear={clearPolicy} isMulti={false} /></div>

		  <div className="grid grid-cols-2 gap-4 mt-4">
			<div>
			  <Label className="text-xs font-semibold text-slate-500">Total Coverage <span className="text-orange-500">*</span></Label>
			  <Input data-testid="vip-input-patient-total-coverage" type="text" value={totalCoverage} onChange={handleTotalChange} placeholder="Enter Total Coverage" className="mt-1.5" />
			  {totalError && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{totalError}</p>}
			</div>
			<div>
			  <Label className="text-xs font-semibold text-slate-500">Current Spending <span className="text-orange-500">*</span></Label>
			  <Input data-testid="vip-input-patient-current-spending" type="number" value={currentSpending} onChange={handleCurrentChange} placeholder="Enter Current Spending" className="mt-1.5" />
			  {currentError && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{currentError}</p>}
			</div>
		  </div>

		  <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #e2e8f0" }}>
			<Label className="text-xs font-semibold text-slate-500">Status</Label>
			<div style={{ display: "flex", gap: 8, marginTop: 8, maxWidth: 260 }}>
			  <button data-testid="vip-btn-patient-status-active" type="button" onClick={() => setPstatus(true)} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 0", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", border: pstatus ? "1px solid #16a34a" : "1px solid #e2e8f0", background: pstatus ? "#f0fdf4" : "#fff", color: pstatus ? "#15803d" : "#64748b" }}>
				<span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} /> Active
			  </button>
			  <button type="button" onClick={() => setPstatus(false)} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 0", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", border: !pstatus ? "1px solid #dc2626" : "1px solid #e2e8f0", background: !pstatus ? "#fef2f2" : "#fff", color: !pstatus ? "#b91c1c" : "#64748b" }}>
				<span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} /> Inactive
			  </button>
			</div>
		  </div>
		</div>
	  </div>
	</ScrollArea>

	<div style={{ padding: "16px 28px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", justifyContent: "flex-end", gap: 10, borderRadius: "0 0 16px 16px", flexShrink: 0 }}>
	  <Button variant="outline" onClick={() => { setEditIndex(null); clear2(); setIsPatDialogOpen(false); }} style={{ borderRadius: 10, fontWeight: 600 }}>Cancel</Button>
	  <Button
		data-testid="vip-btn-save-patient"
		disabled={Boolean(
			validateStrictName(
				firstName,
				"First Name is required.",
				"Name must be at least 3 characters long",
			) ||
			validateStrictName(
				lastName,
				"Last Name is required.",
				"Name must be at least 3 characters long",
			) || checkingPatientIdentifiers || Boolean(duplicatePatient) || Boolean(patientLookupError)
		)}
		onClick={() => { addItem2(); }}
		style={{ background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "#fff", fontWeight: 600, padding: "8px 24px", borderRadius: 10, boxShadow: "0 2px 8px rgba(37,99,235,0.3)" }}
	  >
		<i className="fa-solid fa-plus mr-2"></i>Add Patient
	  </Button>
	</div>
  </DialogContent>
</Dialog>
		</div>
	);
};

export default VIP;